import { Component, Input, OnDestroy, OnInit, computed, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';

import { QaiLoadingComponent } from '@qai/ui';

import { AuthorService } from 'src/app/services/self-serve/author.service';
import { ScoringRefinementService } from 'src/app/services/self-serve/scoring-refinement.service';
import { IGeneratedCallDetails } from 'src/app/interfaces/self-serve/responses/igenerated-call-details';
import { GeneratedCallDetailsComponent } from 'src/app/pages/self-serve/simulation-builder/components/generated-call-details/generated-call-details.component';
import { SelfAuthoringPersonaFormService } from 'src/app/pages/self-serve/simulation-builder/services/self-authoring-persona-form.service';
import { CallRefinementPollingService } from 'src/app/services/self-serve/call-refinement-polling.service';
import { CallRefinementStatus } from 'src/app/utils/enums/self-serve/call-refinement-status.enum';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';

@Component({
  selector: 'generated-simulation-information',
  standalone: true,
  templateUrl: './generated-simulation-information.component.html',
  styleUrls: ['./generated-simulation-information.component.scss'],
  imports: [CommonModule, QaiLoadingComponent, GeneratedCallDetailsComponent, TranslocoModule],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class GeneratedSimulationInformationComponent implements OnInit, OnDestroy {
  @Input() isRefinementBlocked = false;

  details = signal<IGeneratedCallDetails | null>(null);
  loading = signal(false);
  loadError = signal(false);

  private callUuid: string = '';
  /** Carried through to the completed/failed-refinement toast's "View
   * simulation" link — SimulationDetailsComponent's fetch needs this as a
   * query param, or the simulation details page fails to load. */
  private moduleUuid: string = '';
  /** Carried through to the toast's "View simulation" link as a `status`
   * query param — MessagingThemeComponent (scoring tab) reads it to decide
   * whether the simulation is already published. */
  private simulationStatus: string = '';

  /** Raw polling result — drives the refresh trigger below. Never read this
   * from the template; use refinementStatus(), which additionally holds at
   * Thinking while the post-completion content refresh is in flight. */
  private readonly pollingStatus = computed(() =>
    this.callRefinementPollingService.getStatus(this.callUuid),
  );
  readonly isContentRefreshing = signal(false);

  readonly refinementStatus = computed(() => {
    const status = this.pollingStatus();
    if (status === CallRefinementStatus.Updated && this.isContentRefreshing()) {
      return CallRefinementStatus.Thinking;
    }
    return status;
  });

  /** Set to true once the user submits a refinement request this session —
   * guards the Amplitude outcome event so it only fires once per submission,
   * not on re-open when a previous terminal status is still in the record. */
  private refinementSubmittedThisSession = false;

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private selfAuthoringPersonaFormService: SelfAuthoringPersonaFormService,
    private callRefinementPollingService: CallRefinementPollingService,
    private amplitudeService: AmplitudeService,
    private scoringRefinementService: ScoringRefinementService,
  ) {
    effect(() => {
      // React to the raw polling status, not the gated refinementStatus() —
      // that one holds at Thinking *because* isContentRefreshing is true,
      // so reacting to it here would re-trigger a refresh every time it
      // flips back to Updated once the fetch below completes.
      if (this.pollingStatus() === CallRefinementStatus.Updated) {
        this.refreshCallDetailsInPlace();
      }
    });

    effect(() => {
      // Always read reactive signals first so Angular tracks them as dependencies
      // and re-runs the effect when they change — even if the guard below exits early.
      const status = this.pollingStatus();
      if (!this.refinementSubmittedThisSession) return;
      if (status === CallRefinementStatus.Updated) {
        this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.SELF_AUTHORING_REFINEMENT_REQUEST_SUCCEEDED,
          { call_uuid: this.callUuid },
        );
        this.refinementSubmittedThisSession = false;
      } else if (status === CallRefinementStatus.Error) {
        this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.SELF_AUTHORING_REFINEMENT_REQUEST_FAILED,
          { call_uuid: this.callUuid, failure_reason: 'processing_error' },
        );
        this.refinementSubmittedThisSession = false;
      } else if (status === CallRefinementStatus.OutOfScope) {
        this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.SELF_AUTHORING_REFINEMENT_REQUEST_FAILED,
          { call_uuid: this.callUuid, failure_reason: 'out_of_scope' },
        );
        this.refinementSubmittedThisSession = false;
      }
    });
  }

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.moduleUuid = this.route.snapshot.queryParamMap.get('moduleUuid') || '';
    this.simulationStatus = this.route.snapshot.queryParamMap.get('status') || '';
    this.fetchCallDetails();
  }

  ngOnDestroy(): void {
    // "Updated"/"Error" are done — clear them so a future re-open of this
    // callUuid starts clean. A still-running "Thinking" refinement is left
    // untouched: it must keep polling in the background after this view
    // closes (see CallRefinementPollingService class doc).
    if (this.callUuid && this.refinementStatus() !== CallRefinementStatus.Thinking) {
      this.callRefinementPollingService.dismiss(this.callUuid);
    }
  }

  handleRequestChangesSubmitted(value: string): void {
    const callUuid = this.callUuid;
    if (!callUuid) return;

    this.scoringRefinementService.cancelPending();

    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.SELF_AUTHORING_FEEDBACK_SUBMITTED,
      { call_uuid: callUuid, feedback_text: value }
    );

    const callName = this.details()?.title;
    const moduleUuid = this.moduleUuid;
    const simulationStatus = this.simulationStatus;
    const generationUuid = this.details()?.generationUuid;
    this.authorService.requestCallRefinement(callUuid, value).subscribe({
      next: () => {
        this.refinementSubmittedThisSession = true;
        this.callRefinementPollingService.start(callUuid, callName, moduleUuid, simulationStatus, undefined, generationUuid);
      },
      error: (err) => {
        console.error('Failed to request call refinement:', err);
        // The input was disabled optimistically on submit — since no polling
        // record exists to ever flip refinementStatus away from Idle, mark
        // it failed directly so the UI unlocks and shows the error.
        // Set the guard before fail() so the outcome effect fires for this
        // visible failure path too.
        this.refinementSubmittedThisSession = true;
        this.callRefinementPollingService.fail(callUuid, callName, moduleUuid, simulationStatus);
      },
    });
  }

  handleDismissRefinementError(): void {
    this.callRefinementPollingService.dismiss(this.callUuid);
  }

  private fetchCallDetails(): void {
    if (!this.callUuid) return;

    this.loading.set(true);
    this.loadError.set(false);
    this.details.set(null);

    this.authorService.getGeneratedCallDetails(this.callUuid).subscribe({
      next: (response) => {
        this.details.set(response);
        this.loading.set(false);
        this.selfAuthoringPersonaFormService.saveGeneratedDetails(this.callUuid, {
          avatarName: response.avatarName,
          avatarGender: response.avatarGender,
          welcomeMessage: response.welcomeMessage,
          resumeMessage: response.resumeMessage,
        });
      },
      error: (err) => {
        console.error('Failed to fetch generated call details:', err);
        this.loadError.set(true);
        this.loading.set(false);
      },
    });
  }

  /** Swaps in the refreshed content after a refinement completes. Shows the
   * per-section skeleton (isContentRefreshing) instead of the full-view
   * loader/blank state while the fetch is in flight. */
  private refreshCallDetailsInPlace(): void {
    if (!this.callUuid) return;

    this.isContentRefreshing.set(true);

    this.authorService.getGeneratedCallDetails(this.callUuid).subscribe({
      next: (response) => {
        this.details.set(response);
        this.selfAuthoringPersonaFormService.saveGeneratedDetails(this.callUuid, {
          avatarName: response.avatarName,
          avatarGender: response.avatarGender,
          welcomeMessage: response.welcomeMessage,
          resumeMessage: response.resumeMessage,
        });
        this.isContentRefreshing.set(false);
      },
      error: (err) => {
        console.error('Failed to refresh call details after refinement:', err);
        // Keep showing the previous content rather than blanking the view.
        this.isContentRefreshing.set(false);
      },
    });
  }
}
